"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import styles from "./ServiceCountdownBanner.module.css";
import {
  SERVICE_COUNTDOWN_ENDED_GRACE_MS,
  getServiceCountdownStorageKeys,
  persistServiceCountdownDeadline,
} from "@/hooks/useServiceCountdownOfferOverlay";
import { ga4SelectPromotion } from "@/lib/analytics/ga4-ecommerce";

type Props = {
  serviceId: string;
  durationSeconds: number;
  /** With endsAt + duration, must match PackageSection localStorage `version` for the same offer */
  promotionStartsAtMs?: number;
  promotionEndsAtMs?: number;
  /** Seconds already filled on the progress bar when the countdown starts. */
  progressStartOffsetSeconds?: number;
  /** Shown in the offer headline when the countdown is running. */
  discountPercent?: number;
  /** When true, every visitor counts down to the promotional end. */
  usePromotionalEnd?: boolean;
};

function formatPart(value: number): string {
  return value.toString().padStart(2, "0");
}

function getTimeParts(totalSeconds: number) {
  const safeSeconds = Math.max(0, totalSeconds);
  const days = Math.floor(safeSeconds / 86400);
  const hours = Math.floor((safeSeconds % 86400) / 3600);
  const minutes = Math.floor((safeSeconds % 3600) / 60);
  const seconds = safeSeconds % 60;
  return { days, hours, minutes, seconds };
}

function getRemainingSeconds(deadlineMs: number, nowMs: number) {
  return Math.max(0, Math.ceil((deadlineMs - nowMs) / 1000));
}

function FlipUnit({
  value,
  label,
  compact = false,
  urgentHighlight = false,
  pulseTick,
}: {
  value: string;
  label: string;
  compact?: boolean;
  /** Red digits + heartbeat; false keeps default light digit colour (e.g. 00 in D/H/M). */
  urgentHighlight?: boolean;
  pulseTick?: number;
}) {
  const [currentValue, setCurrentValue] = useState(value);
  const [nextValue, setNextValue] = useState(value);
  const [phase, setPhase] = useState<"idle" | "flipTop" | "flipBottom">("idle");
  const [isHeartbeat, setIsHeartbeat] = useState(false);
  const isFlippingRef = useRef(false);
  const topStaticValue = phase === "idle" ? currentValue : nextValue;
  const bottomStaticValue = currentValue;

  useEffect(() => {
    if (value === currentValue) return;
    isFlippingRef.current = true;
    setNextValue(value);
    setPhase("flipTop");

    const topDuration = compact ? 160 : 210;
    const bottomDuration = compact ? 160 : 210;

    const topTimeout = window.setTimeout(() => {
      setPhase("flipBottom");
    }, topDuration);

    const bottomTimeout = window.setTimeout(() => {
      setCurrentValue(value);
      setPhase("idle");
      isFlippingRef.current = false;
    }, topDuration + bottomDuration);

    return () => {
      window.clearTimeout(topTimeout);
      window.clearTimeout(bottomTimeout);
      isFlippingRef.current = false;
    };
  }, [compact, currentValue, value]);

  useEffect(() => {
    if (!urgentHighlight || pulseTick === undefined) return;
    setIsHeartbeat(true);
    const timeout = window.setTimeout(() => setIsHeartbeat(false), compact ? 170 : 210);
    return () => window.clearTimeout(timeout);
  }, [compact, pulseTick, urgentHighlight]);

  return (
    <div
      className={`${styles.flipUnit} ${compact ? styles.compact : ""} ${urgentHighlight ? styles.urgent : ""}`}
    >
      <div className={`${styles.flipFrame} ${isHeartbeat ? styles.heartbeat : ""}`}>
        <div className={`${styles.flipHalf} ${styles.flipTop}`}>
          <span>{topStaticValue}</span>
        </div>
        <div className={`${styles.flipHalf} ${styles.flipBottom}`}>
          <span>{bottomStaticValue}</span>
        </div>

        <div
          className={`${styles.flipHalf} ${styles.flipTop} ${styles.flipAnimTop} ${
            phase === "flipTop" ? styles.isFlippingTop : ""
          }`}
        >
          <span>{currentValue}</span>
        </div>
        <div
          className={`${styles.flipHalf} ${styles.flipBottom} ${styles.flipAnimBottom} ${
            phase === "flipBottom" ? styles.isFlippingBottom : ""
          }`}
        >
          <span>{nextValue}</span>
        </div>

        <div className={styles.hingeLine} />
      </div>
      <div className={styles.flipLabel}>{label}</div>
    </div>
  );
}

export default function ServiceCountdownBanner({
  serviceId,
  durationSeconds,
  promotionStartsAtMs,
  promotionEndsAtMs,
  progressStartOffsetSeconds,
  discountPercent,
  usePromotionalEnd = false,
}: Props) {
  const [isHydrated, setIsHydrated] = useState(false);
  const [isProgressAdvancing, setIsProgressAdvancing] = useState(false);
  const [nowMs, setNowMs] = useState(() => Date.now());
  const [deadlineMs, setDeadlineMs] = useState<number | null>(null);
  const [showSticky, setShowSticky] = useState(false);
  const [stickyTopPx, setStickyTopPx] = useState(72);
  const inlineCountdownRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  useEffect(() => {
    if (!isHydrated) return;
    const now = Date.now();
    if (
      usePromotionalEnd &&
      typeof promotionEndsAtMs === "number" &&
      Number.isFinite(promotionEndsAtMs)
    ) {
      setNowMs(now);
      setDeadlineMs(promotionEndsAtMs);
      return;
    }
    const version = `${promotionStartsAtMs ?? ""}:${promotionEndsAtMs ?? ""}:${durationSeconds}`;
    const createCountdownDeadline = () => now + durationSeconds * 1000;

    let resolvedDeadlineMs = createCountdownDeadline();

    try {
      for (const storageKey of getServiceCountdownStorageKeys(serviceId)) {
        const raw = window.localStorage.getItem(storageKey);
        if (raw) {
          const parsed = JSON.parse(raw) as { deadlineMs?: number; version?: string };
          const storedDeadline = parsed.deadlineMs;
          if (
            typeof storedDeadline === "number" &&
            Number.isFinite(storedDeadline) &&
            storedDeadline > 0 &&
            parsed.version === version
          ) {
            resolvedDeadlineMs = storedDeadline;
            break;
          }
        }
      }
    } catch {
      // ignore malformed storage and regenerate a new local countdown
    }

    setNowMs(now);
    setDeadlineMs(resolvedDeadlineMs);
    persistServiceCountdownDeadline(serviceId, resolvedDeadlineMs, version);
  }, [durationSeconds, isHydrated, promotionEndsAtMs, promotionStartsAtMs, serviceId, usePromotionalEnd]);

  useEffect(() => {
    const syncClock = () => setNowMs(Date.now());
    syncClock();

    const tick = window.setInterval(syncClock, 250);
    window.addEventListener("focus", syncClock);
    window.addEventListener("pageshow", syncClock);
    document.addEventListener("visibilitychange", syncClock);

    return () => {
      window.clearInterval(tick);
      window.removeEventListener("focus", syncClock);
      window.removeEventListener("pageshow", syncClock);
      document.removeEventListener("visibilitychange", syncClock);
    };
  }, [deadlineMs]);

  useEffect(() => {
    let frameId = 0;
    const updateStickyVisibility = () => {
      window.cancelAnimationFrame(frameId);
      frameId = window.requestAnimationFrame(() => {
        const scrollTop = Math.max(
          window.scrollY,
          document.documentElement.scrollTop,
          document.body.scrollTop,
        );
        setShowSticky(scrollTop > 24);
      });
    };

    updateStickyVisibility();
    window.addEventListener("scroll", updateStickyVisibility, { passive: true, capture: true });
    window.addEventListener("resize", updateStickyVisibility);
    return () => {
      window.cancelAnimationFrame(frameId);
      window.removeEventListener("scroll", updateStickyVisibility, true);
      window.removeEventListener("resize", updateStickyVisibility);
    };
  }, []);

  useEffect(() => {
    const calculateStickyTop = () => {
      const siteHeader = document.querySelector<HTMLElement>("[data-site-header]");
      if (siteHeader) {
        if (siteHeader.dataset.navObscured === "true") {
          setStickyTopPx(8);
          return;
        }

        const rect = siteHeader.getBoundingClientRect();
        if (rect.height > 0 && rect.bottom > 0 && rect.top <= 8) {
          setStickyTopPx(Math.max(8, Math.round(rect.bottom + 4)));
          return;
        }
      }

      let maxBottom = 0;
      const candidates = document.querySelectorAll<HTMLElement>("header, nav, [class*='sticky'], [class*='navbar']");

      candidates.forEach((element) => {
        const styles = window.getComputedStyle(element);
        if (styles.position !== "fixed" && styles.position !== "sticky") return;
        if (styles.visibility === "hidden" || styles.display === "none" || Number(styles.opacity) === 0) return;
        const rect = element.getBoundingClientRect();
        const isVisibleTopChrome =
          rect.height > 0 &&
          rect.bottom > 0 &&
          rect.top <= 8 &&
          rect.bottom <= Math.min(window.innerHeight, 160);
        if (isVisibleTopChrome) {
          maxBottom = Math.max(maxBottom, rect.bottom);
        }
      });

      setStickyTopPx(Math.max(8, Math.round(maxBottom + (maxBottom > 0 ? 4 : 0))));
    };

    calculateStickyTop();
    const siteHeader = document.querySelector<HTMLElement>("[data-site-header]");
    const observer =
      typeof MutationObserver !== "undefined" && siteHeader
        ? new MutationObserver(calculateStickyTop)
        : null;
    observer?.observe(siteHeader!, {
      attributes: true,
      attributeFilter: ["data-nav-obscured", "data-nav-past-hero", "data-nav-sticky", "class", "style"],
    });
    window.addEventListener("resize", calculateStickyTop);
    window.addEventListener("scroll", calculateStickyTop, { passive: true });
    return () => {
      observer?.disconnect();
      window.removeEventListener("resize", calculateStickyTop);
      window.removeEventListener("scroll", calculateStickyTop);
    };
  }, []);

  const remainingSeconds = useMemo(
    () => (deadlineMs === null ? 0 : getRemainingSeconds(deadlineMs, nowMs)),
    [deadlineMs, nowMs],
  );
  const parts = useMemo(() => getTimeParts(remainingSeconds), [remainingSeconds]);
  const progressPercent = useMemo(() => {
    if (deadlineMs === null) return 0;
    const totalMs = Math.max(1, durationSeconds * 1000);
    const startOffsetMs = Math.min(
      totalMs,
      Math.max(0, (progressStartOffsetSeconds ?? 0) * 1000),
    );
    const remainingMs = Math.max(0, deadlineMs - nowMs);
    if (remainingMs <= 0) {
      return 100;
    }
    const elapsedMs = Math.min(totalMs, Math.max(0, totalMs - remainingMs));
    const startPercent = (startOffsetMs / totalMs) * 100;
    const elapsedPercent = (elapsedMs / totalMs) * (100 - startPercent);
    return Math.min(100, Math.max(0, startPercent + elapsedPercent));
  }, [deadlineMs, durationSeconds, nowMs, progressStartOffsetSeconds]);

  const previousProgressRef = useRef<number>(0);
  useEffect(() => {
    const isIncreasing = progressPercent > previousProgressRef.current;
    setIsProgressAdvancing(isIncreasing);
    previousProgressRef.current = progressPercent;
  }, [progressPercent]);

  if (deadlineMs === null) {
    return null;
  }

  const isUrgent = remainingSeconds > 0 && remainingSeconds <= 20 * 60;
  const dStr = formatPart(parts.days);
  const hStr = formatPart(parts.hours);
  const mStr = formatPart(parts.minutes);
  const sStr = formatPart(parts.seconds);
  const urgentMinutes = isUrgent && mStr !== "00";
  const urgentSeconds = isUrgent && sStr !== "00";
  const stickyOfferHeadline =
    remainingSeconds <= 0
      ? "OFFER ENDED"
      : discountPercent !== undefined && discountPercent > 0
        ? `${discountPercent}% OFF - LIMITED OFFER`
        : "LIMITED OFFER";
  const stickyLineAboveBar =
    remainingSeconds <= 0 ? stickyOfferHeadline : `${stickyOfferHeadline} ENDS IN`;

  const onLimitedOfferClick = () => {
    ga4SelectPromotion({
      promotion_id: serviceId,
      promotion_name: stickyOfferHeadline,
      creative_name: "countdown_banner",
    });
    document.getElementById("service-package-card")?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  if (deadlineMs > 0 && nowMs >= deadlineMs + SERVICE_COUNTDOWN_ENDED_GRACE_MS) {
    return null;
  }

  const timerBlock = (
    <div className={styles.timerRow}>
      <FlipUnit value={dStr} label="D" />
      <div className={styles.colon}>:</div>
      <FlipUnit value={hStr} label="H" />
      <div className={styles.colon}>:</div>
      <FlipUnit value={mStr} label="M" urgentHighlight={urgentMinutes} pulseTick={remainingSeconds} />
      <div className={styles.colon}>:</div>
      <FlipUnit value={sStr} label="S" urgentHighlight={urgentSeconds} pulseTick={remainingSeconds} />
    </div>
  );

  const stickyTimerBlock = (
    <div className="flex items-start justify-center gap-1 sm:gap-1.5">
      <FlipUnit value={dStr} label="D" compact />
      <div className={`${styles.colon} ${styles.colonCompact}`}>:</div>
      <FlipUnit value={hStr} label="H" compact />
      <div className={`${styles.colon} ${styles.colonCompact}`}>:</div>
      <FlipUnit value={mStr} label="M" compact urgentHighlight={urgentMinutes} pulseTick={remainingSeconds} />
      <div className={`${styles.colon} ${styles.colonCompact}`}>:</div>
      <FlipUnit value={sStr} label="S" compact urgentHighlight={urgentSeconds} pulseTick={remainingSeconds} />
    </div>
  );

  return (
    <>
      <div
        ref={inlineCountdownRef}
        className="mx-auto mb-1 flex w-full cursor-pointer justify-center overflow-x-auto py-0.5"
        aria-live="polite"
        onClick={onLimitedOfferClick}
        onKeyDown={(event) => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            onLimitedOfferClick();
          }
        }}
        role="button"
        tabIndex={0}
      >
        <div className={styles.countdownShell}>
          <div className={styles.progressTrack}>
            <div
              className={`${styles.progressFill} ${isProgressAdvancing ? styles.progressAdvancing : ""}`}
              style={{ width: isHydrated ? `${progressPercent}%` : "0%" }}
            />
          </div>
          {remainingSeconds <= 0 ? (
            <div className="flex flex-col items-center gap-0.5">
              <p
                className="text-center text-sm font-semibold uppercase text-[#ef3c4e] sm:text-base"
                style={{ fontFamily: "Poppins, sans-serif" }}
              >
                OFFER ENDED
              </p>
            </div>
          ) : null}
          {timerBlock}
        </div>
      </div>

      <div
        className={`fixed left-0 right-0 z-40 flex min-h-[74px] cursor-pointer items-center justify-center overflow-x-hidden px-3 py-1.5 transition-all duration-300 sm:min-h-[50px] sm:overflow-x-auto ${
          showSticky ? "translate-y-0 opacity-100" : "translate-y-0 opacity-0 pointer-events-none"
        }`}
        style={{ top: `${stickyTopPx}px` }}
        aria-hidden={!showSticky}
        onClick={showSticky ? onLimitedOfferClick : undefined}
      >
        <div className="flex w-full max-w-[1200px] flex-col items-center justify-center gap-1 py-0.5 sm:flex-row sm:gap-5">
          <div className="flex w-full max-w-[22rem] min-w-0 flex-col items-center gap-0.5 self-center sm:w-auto sm:items-stretch">
            <p
              className="text-center text-[9px] font-semibold uppercase leading-tight tracking-wide text-[#ef3c4e] sm:text-left sm:text-[10px]"
              style={{ fontFamily: "Poppins, sans-serif" }}
            >
              {stickyLineAboveBar}
            </p>
            <div className={`${styles.progressTrack} ${styles.progressTrackCompact}`}>
              <div
                className={`${styles.progressFill} ${isProgressAdvancing ? styles.progressAdvancing : ""}`}
                style={{ width: isHydrated ? `${progressPercent}%` : "0%" }}
              />
            </div>
          </div>
          {stickyTimerBlock}
        </div>
      </div>
    </>
  );
}
